import { DOCUMENT } from '@angular/common';
import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';

/** Una pagina che, lasciata, perderebbe modifiche non salvate. */
export interface UnsavedChangesAware {
  hasUnsavedChanges(): boolean;
}

/**
 * Chiave della conferma di uscita: la pagina che usa il guard monta un `<p-confirm-dialog>` con questa
 * chiave, perché la conferma resti visibile finché la navigazione aspetta la risposta.
 */
export const UNSAVED_CHANGES_CONFIRM_KEY = 'unsavedChanges';

/**
 * Prima di lasciare una pagina con modifiche non salvate chiede conferma, con il ConfirmationService
 * della radice (#2001). Il focus parte da «Resta». Restare (anche chiudendo la finestra o con Esc) rende
 * il focus a chi l'aveva prima, che PrimeNG non ripristina.
 */
export const unsavedChangesGuard: CanDeactivateFn<UnsavedChangesAware> = component => {
  if (!component?.hasUnsavedChanges()) {
    return true;
  }

  const confirmation = inject(ConfirmationService);
  const translate = inject(TranslateService);
  const opener = inject(DOCUMENT).activeElement as HTMLElement | null;
  return new Promise<boolean>(resolve => {
    confirmation.confirm({
      key: UNSAVED_CHANGES_CONFIRM_KEY,
      header: translate.instant('app.unsaved-changes.title'),
      message: translate.instant('app.unsaved-changes.message'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: translate.instant('app.unsaved-changes.leave'),
      rejectLabel: translate.instant('app.unsaved-changes.stay'),
      defaultFocus: 'reject',
      accept: () => resolve(true),
      reject: () => {
        opener?.focus();
        resolve(false);
      },
    });
  });
};
